import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, FileText, Shield, TrendingUp, Clock, Activity, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MetricCard({ title, value, icon: Icon, trend, color, loading }) {
  if (loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-16" />
        </CardContent>
      </Card>
    );
  }

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <Card className="bg-white border-gray-200 hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {trend && (
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3" />
                {trend}
              </p>
            )}
          </div>
          <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center", colorClasses[color])}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/super/dashboard`, {
        headers: getSuperAdminHeader()
      });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bem-vindo ao Painel Administrativo</h2>
          <p className="text-gray-500 mt-1">Gerencie todas as empresas e usuários da plataforma</p>
        </div>
        <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
          <Activity className="h-3 w-3 mr-1" />
          Sistema Online
        </Badge>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard 
          title="Total de Empresas" 
          value={data?.metrics?.total_tenants || 0} 
          icon={Building2}
          color="blue"
          loading={loading}
        />
        <MetricCard 
          title="Total de Usuários" 
          value={data?.metrics?.total_users || 0} 
          icon={Users}
          color="green"
          loading={loading}
        />
        <MetricCard 
          title="Relatórios Gerados" 
          value={data?.metrics?.total_reports || 0} 
          icon={FileText}
          color="purple"
          loading={loading}
        />
        <MetricCard 
          title="Super Admins" 
          value={data?.metrics?.total_super_admins || 0} 
          icon={Shield}
          color="orange"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tenants */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="text-base flex items-center gap-2 text-gray-900">
              <Building2 className="h-5 w-5 text-blue-600" />
              Empresas Recentes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {data?.recent_tenants?.length === 0 && (
                  <p className="text-gray-400 text-center py-8">Nenhuma empresa cadastrada</p>
                )}
                {data?.recent_tenants?.map((tenant) => (
                  <div key={tenant.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{tenant.company_name}</p>
                        <p className="text-xs text-gray-500">{tenant.cnpj || 'CNPJ não informado'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="secondary" className={cn(
                        "text-xs",
                        tenant.is_active !== false 
                          ? "bg-green-50 text-green-700" 
                          : "bg-red-50 text-red-700"
                      )}>
                        {tenant.is_active !== false ? 'Ativo' : 'Inativo'}
                      </Badge>
                      <p className="text-xs text-gray-400 mt-1 flex items-center justify-end gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(tenant.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Users */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="text-base flex items-center gap-2 text-gray-900">
              <Users className="h-5 w-5 text-green-600" />
              Usuários Recentes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {data?.recent_users?.length === 0 && (
                  <p className="text-gray-400 text-center py-8">Nenhum usuário cadastrado</p>
                )}
                {data?.recent_users?.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-medium">
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.full_name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-indigo-50 text-indigo-700">
                      {user.role}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Usage by Tenant */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-base flex items-center gap-2 text-gray-900">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            Uso por Empresa (Relatórios Gerados)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : (
            <div className="space-y-3">
              {data?.usage_by_tenant?.length === 0 && (
                <p className="text-gray-400 text-center py-4">Nenhum dado de uso disponível</p>
              )}
              {data?.usage_by_tenant?.map((item, index) => (
                <div key={item._id} className="flex items-center gap-4">
                  <span className="text-gray-400 w-6 text-sm">#{index + 1}</span>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg transition-all duration-500"
                        style={{ 
                          width: `${Math.min(100, (item.report_count / (data.usage_by_tenant[0]?.report_count || 1)) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-24 text-right">
                    {item.report_count} relatórios
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
